import { X, RadioTower, Split, Boxes, Cable, Users, Share2, Rows3 } from "lucide-react";
import { COLOR_HEX } from "@/lib/modules";

const ICONS = { olt: RadioTower, splitter: Split, closure: Boxes, cable: Cable, client: Users, odf: Rows3 };
const NODE_BG = { olt: "#3B82F6", splitter: "#F97316", closure: "#A855F7", cable: "#0EA5E9", client: "#EF4444", odf: "#10B981" };

export default function SchematicView({ trace, onClose }) {
  if (!trace) return null;
  const fiberColor = COLOR_HEX[trace.client?.fiber_color] || "#22D3EE";
  // Trace steps come client-first; schematic is shown OLT -> ... -> client (left to right)
  const steps = [...(trace.steps || [])].reverse();

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4" data-testid="schematic-view">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-5xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Share2 size={18} className="text-cyan-400" /> Schemat połączeń — {trace.client?.name}</h3>
          <button onClick={onClose} data-testid="schematic-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="overflow-auto p-8">
          <div className="flex items-stretch gap-0 min-w-max mx-auto w-fit">
            {steps.map((s, i) => {
              const Icon = ICONS[s.kind] || Boxes;
              const bg = NODE_BG[s.kind] || "#334155";
              return (
                <div key={i} className="flex items-center">
                  <div className="flex flex-col items-center w-44" data-testid={`schematic-node-${i}`}>
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg" style={{ background: bg }}>
                      <Icon size={28} className="text-white" />
                    </div>
                    <div className="mt-2 text-center">
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">{s.label}</div>
                      <div className="text-sm font-bold">{s.name}</div>
                      <div className="text-[11px] text-slate-400 font-mono leading-tight mt-0.5 max-w-[160px] break-words">{s.detail}</div>
                    </div>
                  </div>
                  {i < steps.length - 1 && (
                    <div className="flex flex-col items-center justify-center -mt-16" style={{ width: 40 }}>
                      <div className="h-1 w-full rounded" style={{ background: fiberColor }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm border-t border-slate-700 pt-5">
            <Metric label="Długość toru" value={`${(trace.total_length_m / 1000).toFixed(2)} km`} />
            <Metric label="Liczba spawów" value={trace.splice_count} />
            <Metric label="Tłumienie toru" value={`${Number(trace.attenuation_db).toFixed(2)} dB`} />
            <Metric label="Włókno" value={`${trace.client?.fiber_number ?? "-"} (${trace.client?.fiber_color ?? "-"})`} color={fiberColor} />
            {trace.dist_to_olt_m != null && <Metric label="Do OLT" value={`${(trace.dist_to_olt_m / 1000).toFixed(2)} km`} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="font-mono font-bold" style={{ color: color || "#F8FAFC" }}>{value}</div>
    </div>
  );
}
